import React from 'react';
import styled, { css } from 'styled-components';
import { themeColor } from '../../theme/colors';

import { DetectionParameter } from '../types';
import { ThresholdFilter } from '../../Filters/ThresholdFilter';
import ThresholdFilterStyles from '../../Filters/ThresholdFilter.styles';
import { SingleDetectionParameterProps } from '../DetectionParametersGroup';
import { ellipsisMixin } from '../../theme/utils';
import { smRegular } from '../../theme/typography';

export const NumberDetectionParameter: React.FC<
  SingleDetectionParameterProps
> = ({
  detectionParameter,
  descriptors,
  readonly,
  onParameterChange,
  fullWidth,
  dirty,
  role,
}) => {
  function handleOperatorChange(operator: DetectionParameter['operator']) {
    onParameterChange?.({ ...detectionParameter, operator });
  }

  function handleValueChange(value: number | null) {
    onParameterChange?.({ ...detectionParameter, value });
  }

  return (
    <StyledThresholdFilter
      icon={descriptors[detectionParameter.id].icon || 'metrics.numberedList'}
      label={descriptors[detectionParameter.id].label}
      tooltip={descriptors[detectionParameter.id].tooltip}
      unit={descriptors[detectionParameter.id].unit}
      presetValues={descriptors[detectionParameter.id].presetValues}
      value={detectionParameter.value}
      operator={detectionParameter.operator}
      readonly={readonly}
      readonlyOperator={true}
      nullable
      onValueChange={(value) => handleValueChange(value)}
      onOperatorChange={(operator) => handleOperatorChange(operator)}
      options={{
        closePopoverOnPresetChange: true,
      }}
      $fullWidth={fullWidth}
      $dirty={dirty}
      debounce={false}
      role={role}
      maxLength={14}
    />
  );
};

const StyledThresholdFilter = styled(ThresholdFilter)<{
  $fullWidth?: boolean;
  $dirty?: boolean;
}>`
  ${smRegular};
  width: inherit;
  max-width: 100%;
  cursor: pointer;

  background: ${themeColor('buttonBackgroundHover')};
  border-color: ${themeColor('strokePrimary')};

  ${(props) =>
    props.$fullWidth &&
    css`
      flex: 1;
      width: auto;
      padding-bottom: 4px;
      padding-top: 4px;
      & ${ThresholdFilterStyles.Label} {
        flex: 1;
      }

      & ${ThresholdFilterStyles.PresetTrigger} {
        min-width: 80px;
        justify-content: center;
      }
    `}

  & ${ThresholdFilterStyles.OperatorButton} {
    background: inherit;
  }

  ${(props) =>
    props.$dirty &&
    css`
      & ${ThresholdFilterStyles.ValueInput} {
        color: ${themeColor('turquoise')};
      }

      & ${ThresholdFilterStyles.Unit} {
        color: ${themeColor('turquoise')};
      }
    `}

  & ${ThresholdFilterStyles.Label} {
    cursor: pointer;
    color: ${themeColor('textPrimary')};
    ${ellipsisMixin};
  }

  & ${ThresholdFilterStyles.LabelContainer} {
    flex: 1;

    justify-content: center;
  }

  & ${ThresholdFilterStyles.ValueInput} {
    width: min-content;
    white-space: nowrap;
    overflow: initial;
    text-overflow: initial;

    text-align: right;
    padding-inline-end: 0;
    padding-inline-start: 0;
  }
`;
